import { useCallback, useEffect, useRef, useState } from 'react';
import { Panel } from '../../../components/panels/Panel';
import { VERALLIA_MONITORING_CONFIG } from '../../../config/monitoring/verallia-monitoring-config';
import { getVeralliaMonitoringStatus } from '../../../services/monitoring/monitoring-service';

const STATUS_STYLES = {
  ok: { dot: 'bg-green-400', text: 'text-green-400', border: 'border-green-500/40', background: 'bg-green-500/10' },
  warn: { dot: 'bg-orange-400', text: 'text-orange-400', border: 'border-orange-500/40', background: 'bg-orange-500/10' },
  danger: { dot: 'bg-red-400', text: 'text-red-400', border: 'border-red-500/40', background: 'bg-red-500/10' },
  none: { dot: 'bg-perception-gray-600', text: 'text-perception-gray-500', border: 'border-perception-gray-700/40', background: 'bg-perception-black-500' },
};

const getStatusStyle = (state) => STATUS_STYLES[state] ?? STATUS_STYLES.none;

const formatTimestamp = (value) => {
  if (!value) return 'Never';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown time' : date.toLocaleString();
};

const describeTimelineSegment = (segment) => {
  if (!segment) return 'Hover a segment to see details.';
  const start = new Date(segment.start);
  const end = new Date(segment.end);
  const timeLabel = segment.start && segment.end && !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())
    ? `${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    : 'Unknown time';
  return segment.state === 'none' ? `${timeLabel} · No data` : `${timeLabel} · ${segment.dominant_label || segment.state || 'none'}`;
};

const StatusDot = ({ state, pulse = false }) => (
  <span className={`relative inline-block size-3 shrink-0 rounded-full ${getStatusStyle(state).dot}`}>
    {pulse && <span className="absolute -inset-1 animate-ping rounded-full border border-green-400/50"/>}
  </span>
);

const StateBadge = ({ state, children }) => {
  const style = getStatusStyle(state);
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${style.text} ${style.border} ${style.background}`}>{children}</span>;
};

const Timeline = ({ device, onSegmentMove, onLeave }) => {
  const segments = device.timeline ?? [];

  return (
    <div className="flex h-2.5 w-full min-w-0 gap-[2px]" onMouseLeave={onLeave}>
      {segments.length === 0 ? <span className="h-full flex-1 rounded-sm bg-perception-gray-700/40"/> : segments.map((segment, index) => (
        <span key={index} className={`h-full min-w-px flex-1 cursor-help rounded-[2px] ${segment.state === 'up' || segment.state === 'ok' ? 'bg-green-400' : segment.state === 'partial' || segment.state === 'warn' ? 'bg-orange-400' : segment.state === 'down' || segment.state === 'danger' ? 'bg-red-400' : 'bg-perception-gray-700/40'}`} onMouseMove={(event) => onSegmentMove(event, device, segment)}/>
      ))}
    </div>
  );
};

const MonitoringDeviceRow = ({ device, onSegmentMove, onLeave }) => {
  const state = device.state_class || 'none';

  return (
    <tr className="border-t border-perception-black-500 text-sm text-perception-gray-300 hover:bg-perception-black-700/80">
      <td className="px-4 py-3">
        <div className="font-semibold text-white">{device.name || device.id}</div>
        <div className="mt-0.5 font-mono text-xs text-perception-gray-600">{device.id}</div>
      </td>

      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <StatusDot state={state} pulse={state === 'ok'}/>
          <StateBadge state={state}>{device.state_label || 'No data'}</StateBadge>
        </div>
      </td>

      <td className={`px-4 py-3 text-xs ${device.stale ? 'text-perception-gray-600' : 'text-perception-gray-300'}`}>
        {formatTimestamp(device.last_seen)}
      </td>

      <td className="px-4 py-3">
        {device.ai_monitored ? (
          <div className="flex items-center gap-3">
            <StatusDot state={device.ai_running ? 'ok' : 'danger'} pulse={!!device.ai_running}/>
            <StateBadge state={device.ai_running ? 'ok' : 'danger'}>{device.ai_running ? 'Running' : 'Stopped'}</StateBadge>
          </div>
        ) : <span className="text-perception-gray-600">—</span>}
      </td>

      <td className={`px-4 py-3 text-xs ${device.ai_monitored ? 'text-perception-gray-300' : 'text-perception-gray-600'}`}>
        {device.ai_monitored ? formatTimestamp(device.ai_last_run) : '—'}
      </td>

      <td className="px-4 py-3">
        <Timeline device={device} onSegmentMove={onSegmentMove} onLeave={onLeave}/>
      </td>
    </tr>
  );
};

export const MonitoringPage = () => {
  const [status, setStatus] = useState(null);
  const [hasError, setHasError] = useState(false);
  const tooltipRef = useRef(null);

  useEffect(() => {
    let active = true;
    let inFlight = false;

    const refresh = async () => {
      if (inFlight) return;
      inFlight = true;

      try {
        const data = await getVeralliaMonitoringStatus();
        if (active) { setStatus(data); setHasError(false); }
      } catch (error) {
        if (active) { setHasError(true); console.error('Unable to load monitoring status.', error); }
      } finally {
        inFlight = false;
      }
    };

    refresh();
    const interval = window.setInterval(refresh, VERALLIA_MONITORING_CONFIG.refreshIntervalMs);
    return () => { active = false; window.clearInterval(interval); };
  }, []);

  const handleSegmentMove = useCallback((event, device, segment) => {
    const tooltip = tooltipRef.current;
    if (!tooltip) return;

    tooltip.textContent = `${device.name || device.id || 'Device'} · ${describeTimelineSegment(segment)}`;
    tooltip.classList.remove('hidden');

    const width = tooltip.getBoundingClientRect().width;
    const left = Math.max(10, Math.min(event.clientX + 12, window.innerWidth - width - 10));
    const top = event.clientY > tooltip.offsetHeight + 22 ? event.clientY - tooltip.offsetHeight - 12 : event.clientY + 12;

    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
  }, []);

  const hideTooltip = useCallback(() => tooltipRef.current?.classList.add('hidden'), []);

  const storage = status?.storage;
  const storageValid = storage?.used_gb != null && storage?.free_gb != null && storage?.total_gb != null;
  const devices = status?.devices ?? [];
  const service = status?.service;
  const diskStyle = getStatusStyle(storage?.state || 'none');

  return (
    <Panel title="SUPERVISIÓN" overflowAuto={false}>
      <div className="flex h-full min-h-0 flex-col gap-3 p-3">
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-4 rounded-md bg-perception-black-500 px-5 py-3">
          <div className="flex items-center gap-4">
            <span className="size-3 shrink-0 rounded-full bg-teal-400 shadow-[0_0_12px_rgba(45,212,191,0.5)]"/>
            <div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <div className="flex h-7 items-center text-lg font-semibold leading-none text-white">
                  Estado del sistema
                </div>
                <div className="flex h-7 items-center font-mono text-xs leading-none text-perception-gray-600">
                  {hasError ? 'Última actualización: error' : status?.generated_at ? `Última actualización: ${formatTimestamp(status.generated_at)}` : 'Cargando...'}
                </div>
              </div>
              <div className="mt-1 flex items-center gap-2 text-xs text-perception-gray-500">
                <span>Estado general de la infraestructura</span><span>·</span>
                {service ? (
                  <span className={service.alive ? 'text-green-400' : 'text-red-400'}>
                    {service.alive ? 'Servicio activo' : 'Servicio inaccesible'}
                  </span>
                ) : <span>Cargando...</span>}
              </div>
            </div>
          </div>

          <div className="rounded-md bg-perception-black-700 px-4 py-2">
            <div className={`text-[11px] font-semibold uppercase tracking-wider ${diskStyle.text}`}>Almacenamiento del PC</div>
            <div className="mt-1 font-mono text-xs text-perception-gray-300">
              {storageValid ? `${storage.used_gb} GB used / ${storage.total_gb} GB · ${storage.free_gb} GB available · ${storage.used_percent}% used` : 'No data'}
            </div>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md bg-perception-black-700">
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-perception-black-500 px-4 py-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-perception-gray-300">Resumen de infraestructura</h2>

            <div className="flex flex-wrap items-center gap-5 text-xs text-perception-gray-600">
              <span>Actualización automática cada minuto</span>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1.5"><span className="size-2 rounded-sm bg-green-400"/>Saludable</span>
                <span className="flex items-center gap-1.5"><span className="size-2 rounded-sm bg-orange-400"/>Advertencia</span>
                <span className="flex items-center gap-1.5"><span className="size-2 rounded-sm bg-red-400"/>Crítico</span>
              </div>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
            <table className="w-full min-w-[1030px] table-fixed text-left">
              <colgroup>
                <col className="w-[20%]"/>
                <col className="w-[14%]"/>
                <col className="w-[15%]"/>
                <col className="w-[15%]"/>
                <col className="w-[15%]"/>
                <col className="w-[21%]"/>
              </colgroup>

              <thead className="sticky top-0 z-[1] bg-perception-black-500 text-xs font-semibold uppercase tracking-wide text-perception-gray-500">
              <tr>
                <th className="px-4 py-3">Dispositivo</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Última conexión</th>
                <th className="px-4 py-3">IA en ejecución</th>
                <th className="px-4 py-3">Última ejecución</th>
                <th className="px-4 py-3">Disponibilidad hoy</th>
              </tr>
              </thead>

              <tbody>
              {hasError ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-perception-gray-600">
                    No se ha podido cargar el estado del sistema.
                  </td>
                </tr>
              ) : devices.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-perception-gray-600">
                    {status ? 'Sin dispositivos' : 'Cargando datos...'}
                  </td>
                </tr>
              ) : devices.map((device, index) => (
                <MonitoringDeviceRow key={`${device.id ?? 'device'}-${index}`} device={device} onSegmentMove={handleSegmentMove} onLeave={hideTooltip}/>
              ))}
              </tbody>
            </table>
          </div>
        </div>

        <div ref={tooltipRef} role="tooltip" className="pointer-events-none fixed z-[100] hidden max-w-[310px] whitespace-nowrap rounded-md border border-perception-black-500 bg-perception-black-900 px-3 py-2 font-mono text-xs text-white shadow-lg"/>
      </div>
    </Panel>
  );
};
